<script setup>
import { reactiveOmit } from '@vueuse/core';
import { computed } from 'vue';
import { Check, Minus } from 'lucide-vue-next';
import { CheckboxIndicator, CheckboxRoot, useForwardPropsEmits } from 'reka-ui';
import { cn } from '@/lib/utils';

const props = defineProps({
    /** Controlled checked state */
    checked: { type: Boolean, default: undefined },
    /** Controlled indeterminate state */
    indeterminate: { type: Boolean, default: undefined },
    /** Show validation error styling */
    invalid: { type: Boolean, default: false },
    defaultValue: { type: [Boolean, String], required: false },
    modelValue: { type: [Boolean, String, null], required: false },
    disabled: { type: Boolean, required: false },
    value: { type: null, required: false },
    id: { type: String, required: false },
    asChild: { type: Boolean, required: false },
    as: { type: null, required: false },
    name: { type: String, required: false },
    required: { type: Boolean, required: false },
    class: { type: null, required: false }
});
const emits = defineEmits(['update:modelValue']);

// omit styling and control props; modelValue/defaultValue will be set from checked/indeterminate
const delegatedProps = reactiveOmit(props, ['class', 'checked', 'indeterminate', 'invalid', 'modelValue', 'defaultValue']);

const forwarded = useForwardPropsEmits(delegatedProps, emits);

// derive tristate value for underlying CheckboxRoot
const resolvedState = computed(() => {
    if (props.indeterminate) return 'indeterminate';
    if (props.checked !== undefined) return props.checked;
    return props.modelValue;
});
</script>

<template>
    <CheckboxRoot
        data-slot="checkbox"
        v-bind="forwarded"
        :modelValue="resolvedState"
        :defaultValue="resolvedState"
        :aria-invalid="props.invalid"
        :class="
            cn(
                'peer data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground data-[state=indeterminate]:border-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
                props.class
            )
        ">
        <CheckboxIndicator data-slot="checkbox-indicator" class="flex items-center justify-center text-current transition-none">
            <slot>
                <template v-if="props.indeterminate">
                    <Minus class="size-3.5 text-primary-foreground" />
                </template>
                <template v-else>
                    <Check class="size-3.5 data-[state=checked]:text-primary-foreground" />
                </template>
            </slot>
        </CheckboxIndicator>
    </CheckboxRoot>
</template>
